// ============================================================
// Invoice SPK — pembayaran DP/Lunas, cash/transfer, kembalian
// ============================================================

import Alpine from 'alpinejs';

const STATUS_BADGES = {
  LUNAS: 'badge-green',
  DP: 'badge-orange',
  BB: 'badge-gray',
};

Alpine.data('spkInvoice', (config = {}) => ({
  rows: config.rows || [],
  meta: config.meta || { total: 0 },
  filters: {
    status_bayar: config.filters?.status_bayar || '',
    metode: config.filters?.metode || '',
    search: config.filters?.search || '',
  },
  invoice: null,
  searchId: '',
  payJenis: 'lunas',
  payAmount: 0,
  payMetode: 'cash',
  payBank: 'BRI - Rena Delia',
  payDiskonType: 'nominal',
  payDiskonValue: 0,
  paying: false,
  loadingList: false,
  lastKembalian: 0,
  paymentCompleted: false,
  printViewUrl: '',
  whatsappUrl: '',
  waSent: false,
  sendingWa: false,

  get sisaBayar() {
    return Number(this.invoice?.summary?.sisa_bayar || 0);
  },

  get payDiskonAmount() {
    const sisa = this.sisaBayar;
    const value = Number(this.payDiskonValue || 0);
    if (value <= 0 || sisa <= 0) return 0;
    if (this.payDiskonType === 'percent') {
      return Math.floor(sisa * Math.min(100, value) / 100);
    }
    return Math.min(sisa, Math.floor(value));
  },

  get effectiveSisa() {
    return Math.max(0, this.sisaBayar - this.payDiskonAmount);
  },

  get kembalian() {
    if (this.payJenis !== 'lunas') return 0;
    const diff = Number(this.payAmount || 0) - this.effectiveSisa;
    return diff > 0 ? diff : 0;
  },

  get maxDpAmount() {
    // DP wajib di bawah sisa (setelah diskon)
    return Math.max(0, this.effectiveSisa - 1);
  },

  get canSubmitPayment() {
    if (!this.invoice || this.invoice.summary.status === 'LUNAS') return false;
    const amount = Number(this.payAmount || 0);
    if (amount <= 0) return false;
    if (this.payJenis === 'lunas' && amount < this.effectiveSisa) return false;
    if (this.payJenis === 'dp') {
      if (this.effectiveSisa <= 0) return false;
      if (amount > this.maxDpAmount) return false;
      if (amount + this.payDiskonAmount >= this.sisaBayar) return false;
    }
    if (this.payMetode === 'transfer' && !this.payBank) return false;
    return true;
  },

  /** Cetak ulang & kirim WA untuk invoice DP atau Lunas */
  get showInvoiceActions() {
    const status = this.invoice?.summary?.status;
    return status === 'LUNAS' || status === 'DP';
  },

  get paymentHint() {
    if (!this.invoice || this.invoice.summary.status === 'LUNAS') return '';
    const amount = Number(this.payAmount || 0);
    if (amount <= 0 && this.payDiskonAmount <= 0) {
      return 'Masukkan jumlah yang diterima dari pelanggan.';
    }
    if (this.payDiskonAmount > 0) {
      const parts = [`Diskon: Rp ${this.formatRP(this.payDiskonAmount)}`];
      if (this.effectiveSisa > 0) {
        parts.push(`Sisa setelah diskon: Rp ${this.formatRP(this.effectiveSisa)}`);
      }
      if (this.payJenis === 'lunas' && amount > 0 && amount < this.effectiveSisa) {
        parts.push(`Kurang Rp ${this.formatRP(this.effectiveSisa - amount)}`);
      }
      return parts.join(' · ');
    }
    if (this.payJenis === 'lunas' && amount < this.effectiveSisa) {
      return `Kurang Rp ${this.formatRP(this.effectiveSisa - amount)} — lunas tidak bisa diselesaikan.`;
    }
    if (this.payJenis === 'dp' && this.effectiveSisa <= 0) {
      return 'Sisa tagihan sudah 0 — gunakan jenis Lunas jika perlu konfirmasi.';
    }
    if (this.payJenis === 'dp' && amount >= this.effectiveSisa) {
      return `DP harus di bawah sisa tagihan (maks. Rp ${this.formatRP(this.maxDpAmount)}). Untuk lunas penuh, pilih Lunas.`;
    }
    if (this.payJenis === 'dp' && amount + this.payDiskonAmount >= this.sisaBayar) {
      return 'DP + diskon tidak boleh menutup seluruh tagihan. Pilih Lunas untuk pelunasan.';
    }
    if (this.kembalian > 0) {
      return `Kembalian: Rp ${this.formatRP(this.kembalian)}`;
    }
    return '';
  },

  async initPage() {
    const id = new URLSearchParams(window.location.search).get('id');
    if (id) {
      await this.loadInvoice(id);
    }
  },

  async fetchList() {
    this.loadingList = true;
    try {
      const params = new URLSearchParams();
      if (this.filters.status_bayar) params.set('status_bayar', this.filters.status_bayar);
      if (this.filters.metode) params.set('metode', this.filters.metode);
      if (this.filters.search) params.set('search', this.filters.search);
      const { data } = await axios.get(`/api/v1/kasir/spk/invoices?${params}`);
      this.rows = data.data?.rows || [];
      this.meta = data.data?.meta || { total: this.rows.length };
    } catch {
      window.toast?.('Gagal memuat daftar invoice.', 'error');
    } finally {
      this.loadingList = false;
    }
  },

  async applyFilters() {
    await this.fetchList();
  },

  async resetFilters() {
    this.filters = { status_bayar: '', metode: '', search: '' };
    await this.fetchList();
  },

  async loadInvoice(id) {
    if (!id) return;
    try {
      const { data } = await axios.get(`/api/v1/kasir/spk/invoice/${id}`);
      this.invoice = data.data;
      this.searchId = String(id);
      this.payDiskonType = 'nominal';
      this.payDiskonValue = 0;
      this.payJenis = this.invoice.summary.status === 'BB' ? 'dp' : 'lunas';
      this.payAmount = this.payJenis === 'lunas'
        ? Number(this.invoice.summary.sisa_bayar || 0)
        : 0;
      this.lastKembalian = 0;
      this.paymentCompleted = false;
      this.printViewUrl = this.invoice.print_url || '';
      this.whatsappUrl = this.invoice.whatsapp_url || '';
      this.waSent = !!this.invoice.wa_sent;
      this.paymentCompleted = this.invoice.summary?.status === 'LUNAS';
    } catch {
      window.toast?.('Invoice tidak ditemukan.', 'error');
    }
  },

  selectRow(row) {
    const ref = row.document_number || row.spk_number || row.legacy_number;
    this.loadInvoice(ref);
  },

  applyLunasAmount() {
    if (this.payJenis === 'lunas') {
      this.payAmount = this.effectiveSisa;
      return;
    }

    // DP: jangan biarkan jumlah = total/sisa
    const amount = Number(this.payAmount || 0);
    if (amount >= this.effectiveSisa && this.effectiveSisa > 0) {
      this.payAmount = this.maxDpAmount;
    }
  },

  statusBadge(label) {
    return STATUS_BADGES[label] || 'badge-gray';
  },

  metodeLabel(metode) {
    if (!metode) return '—';
    return metode === 'cash' ? 'Cash' : 'Transfer';
  },

  openPrintWindow() {
    const url = this.printViewUrl || this.invoice?.print_url;
    if (!url) return;
    window.open(url, 'PRINT_INVOICE', 'width=420,height=760');
  },

  async submitPayment() {
    if (!this.canSubmitPayment || !this.invoice) return;
    this.paying = true;
    try {
      const info = this.invoice.invoice_info || {};
      // Backend menerima legacy int, nomor USP, atau UUID.
      // Jangan kirim document_number sebagai integer-only field.
      const spkId = info.legacy_id ?? info.id ?? info.spk_uuid;
      const { data } = await axios.post('/api/v1/kasir/spk/payment', {
        spk_id: spkId,
        jenis: this.payJenis,
        jumlah_bayar: Number(this.payAmount),
        metode: this.payMetode,
        bank: this.payMetode === 'transfer' ? this.payBank : '',
        diskon_type: this.payDiskonValue > 0 ? this.payDiskonType : null,
        diskon_value: this.payDiskonValue > 0 ? Number(this.payDiskonValue) : null,
        diskon: this.payDiskonAmount,
      });
      this.lastKembalian = data.data?.kembalian || 0;
      this.invoice = data.data?.invoice || this.invoice;
      this.printViewUrl = data.data?.print_url || this.invoice?.print_url || '';
      this.whatsappUrl = data.data?.whatsapp_url || this.invoice?.whatsapp_url || '';
      this.waSent = !!(data.data?.wa_sent || this.invoice?.wa_sent);
      this.paymentCompleted = true;

      const status = this.invoice?.summary?.status;
      const waAuto = data.data?.whatsapp_auto;
      let msg = this.lastKembalian > 0
        ? `Pembayaran berhasil! Kembalian Rp ${this.formatRP(this.lastKembalian)}`
        : 'Pembayaran berhasil!';
      if (status === 'LUNAS' && waAuto?.queued) {
        msg += ' Invoice dikirim otomatis ke WhatsApp pelanggan (Fonnte).';
      } else if (status === 'LUNAS' && waAuto && !waAuto.queued && waAuto.message) {
        msg += ` (${waAuto.message})`;
      }
      window.toast?.(msg, 'success');

      this.openPrintWindow();
      await this.fetchList();
    } catch (e) {
      window.toast?.(e.response?.data?.message || 'Gagal menyimpan pembayaran.', 'error');
    } finally {
      this.paying = false;
    }
  },

  reprintReceipt() {
    this.openPrintWindow();
  },

  async sendWhatsapp() {
    if (!this.invoice) return;
    const info = this.invoice.invoice_info || {};
    const reference = info.id || info.legacy_id || info.spk_uuid || this.searchId;
    if (!reference) {
      window.toast?.('Referensi invoice tidak ditemukan.', 'warning');
      return;
    }

    this.sendingWa = true;
    try {
      const { data } = await axios.post(`/api/v1/kasir/spk/invoice/${encodeURIComponent(reference)}/send-whatsapp`);
      this.waSent = true;
      if (data.data?.invoice) {
        this.invoice = data.data.invoice;
      }
      this.whatsappUrl = data.data?.whatsapp_url || this.whatsappUrl;
      window.toast?.(data.message || 'Invoice dikirim ke WhatsApp via Fonnte.', 'success');
    } catch (e) {
      const fallback = e.response?.data?.errors?.whatsapp_url
        || this.whatsappUrl
        || this.invoice?.whatsapp_url;
      window.toast?.(e.response?.data?.message || 'Gagal kirim via Fonnte.', 'error');
      if (fallback && fallback !== '#') {
        window.open(fallback, '_blank', 'noopener');
      }
    } finally {
      this.sendingWa = false;
    }
  },

  formatRP(n) {
    return Number(n || 0).toLocaleString('id-ID');
  },
}));
